import { addToast } from '@heroui/react';
import axios, {
  AxiosError, AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig,
} from 'axios';
import { signOut } from 'firebase/auth';
import { TriangleAlert } from 'lucide-react';
import { createElement } from 'react';

import { auth } from '../config/firebase';
import { isStaffOnlyError, useStaffOnlyStore } from '../route/StaffOnlyGate/staffOnlyStore';
import { i18n } from '../utils/i18n';
import { buildLocalePath } from '../utils/locale';
import { compactToastProps, TOAST_TIMEOUT } from '../utils/toastStyles';
import { shouldShowAiLimitToast } from './aiLimit';
import { createUnauthorizedHandler, RetriableRequest } from './sessionCheck';

const baseURL = import.meta.env.VITE_URL ? import.meta.env.VITE_URL : 'https://europe-west1-hotelspot-dev.cloudfunctions.net';

export const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Żądania, którym Authorization dołożył interceptor (token currentUser). Tylko je wolno
// ponowić z odświeżonym tokenem; nagłówek ustawiony ręcznie przez wołającego zostaje.
const autoAuthRequests = new WeakSet<object>();

// Dołącza Firebase ID token (jeśli brak ręcznie ustawionego) — backend v3 autoryzuje.
axiosInstance.interceptors.request.use(async (config) => {
  if (!config.headers.Authorization) {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      autoAuthRequests.add(config);
    }
  }
  return config;
});

let isAILimitToastVisible = false;

export const isAILimitToastActive = (): boolean => isAILimitToastVisible;

const warningToastProps = compactToastProps('warning');

type SessionRetryConfig = InternalAxiosRequestConfig & RetriableRequest;

// 401 wylogowuje TYLKO przy faktycznie nieważnej sesji Firebase (szczegóły: sessionCheck.ts).
// Błąd jednego endpointu (np. AI) odrzuca żądanie, a wołający pokazuje komunikat.
const handleUnauthorized = createUnauthorizedHandler<SessionRetryConfig>({
  getUser: () => auth.currentUser,
  isAutoAuth: (config) => autoAuthRequests.has(config),
  retry: (config, token) => axiosInstance({
    ...config,
    sessionRetried: true,
    headers: AxiosHeaders.concat(config.headers, { Authorization: `Bearer ${token}` }),
  } as SessionRetryConfig),
  isOnLoginPage: () => window.location.pathname.includes('/login'),
  onSessionInvalid: () => {
    addToast({
      ...warningToastProps,
      title: i18n.t('auth.sessionExpired.title', 'Sesja wygasła'),
      description: i18n.t('auth.sessionExpired.description', 'Zaloguj się ponownie.'),
      timeout: TOAST_TIMEOUT.long,
      icon: createElement(TriangleAlert, { size: 16, strokeWidth: 2 }),
    });
    return signOut(auth).catch(() => {}).finally(() => {
      const locale = i18n.language || 'pl';
      window.history.pushState({}, '', buildLocalePath('/login', locale));
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  },
});

// Response interceptor: sesja (401), konto tylko Staff (403) i limit AI (429).
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      return handleUnauthorized(error, error.config as SessionRetryConfig | undefined);
    }

    // Konto tylko do aplikacji Staff (#82): backend odcina endpointy panelu, panel
    // pokazuje zamiast treści ekran „Brak dostępu do panelu” (StaffOnlyGate).
    if (isStaffOnlyError(error.response?.status, error.response?.data)) {
      useStaffOnlyStore.getState().setBlocked(true);
      return Promise.reject(error);
    }

    if (error.response?.status === 429) {
      const data = error.response.data as any;

      // Brak puli AI (`ai_pool_required`) przechodzi dalej z `message`: komunikat pokazuje wywołujący.
      if (shouldShowAiLimitToast(error.response.status, data, error.config)) {
        isAILimitToastVisible = true;

        const titleKey = data?.titleKey || 'aiUsage.limitExceeded.title';
        const descKey = data?.descriptionKey || 'aiUsage.limitExceeded.description';

        // Bez przycisku „zmień plan" — ekran zmiany planu został usunięty,
        // więc zostaje sama informacja o wyczerpanym limicie.
        const descriptionWithButton = createElement(
          'div',
          null,
          createElement('span', null, i18n.t(descKey)),
        );

        addToast({
          ...warningToastProps,
          title: i18n.t(titleKey),
          // @ts-expect-error HeroUI types description as string but renders ReactNode fine
          description: descriptionWithButton,
          timeout: 10000,
          icon: createElement(TriangleAlert, { size: 16, strokeWidth: 2 }),
        });

        const handled = new Error('AI_LIMIT_HANDLED') as any;
        handled._aiLimitHandled = true;
        return Promise.reject(handled);
      }
    }

    return Promise.reject(error);
  },
);
